import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import DealCrawlerService, { CruiseDealCrawler, CruiseDealSailingCrawler } from '~/services/cruises/DealCrawlerService';

import { dealCrawlerQueryKeys } from './dealCrawlerQueryKeys';

export type DealCrawlerByIdMap = Record<string, CruiseDealCrawler>;
export type DealCrawlerSailingByIdMap = Record<string, Array<CruiseDealSailingCrawler>>;

async function fetchOneDealCrawlerAndSailings(
  crawlerId: string,
  signal?: AbortSignal,
): Promise<{ crawler: CruiseDealCrawler | null; sailings: Array<CruiseDealSailingCrawler> }> {
  const requestOptions = signal ? { signal } : undefined;
  const [crawlerRes, sailingsRes] = await Promise.all([
    DealCrawlerService.getById(crawlerId, requestOptions),
    DealCrawlerService.getSailings(crawlerId, {}, requestOptions),
  ]);
  return {
    crawler: crawlerRes?.result ?? null,
    sailings: sailingsRes?.result ?? [],
  };
}

export function useDealCrawlerByIds(ids: Array<string>) {
  const normalizedIds = useMemo(() => {
    const filtered = ids.filter(Boolean);
    return Array.from(new Set(filtered)).sort();
  }, [ids]);

  const queries = useQueries({
    queries: normalizedIds.map((crawlerId) => ({
      queryKey: dealCrawlerQueryKeys.detail(crawlerId),
      queryFn: ({ signal }: { signal?: AbortSignal }) => fetchOneDealCrawlerAndSailings(crawlerId, signal),
      enabled: true,
    })),
  });

  const { dealCrawlerById, dealCrawlerSailingById } = useMemo(() => {
    const byId: DealCrawlerByIdMap = {};
    const sailingById: DealCrawlerSailingByIdMap = {};
    normalizedIds.forEach((crawlerId, index) => {
      const result = queries[index]?.data;
      if (result?.crawler) {
        byId[crawlerId] = result.crawler;
      }
      if (result?.sailings) {
        sailingById[crawlerId] = result.sailings;
      }
    });
    return { dealCrawlerById: byId, dealCrawlerSailingById: sailingById };
  }, [normalizedIds, queries]);

  const isLoading = queries.some((q) => q.isLoading);
  const isFetching = queries.some((q) => q.isFetching);
  const isError = queries.some((q) => q.isError);
  const error = queries.find((q) => q.error)?.error;

  return {
    data: Object.keys(dealCrawlerById).length > 0 ? { dealCrawlerById, dealCrawlerSailingById } : undefined,
    isLoading,
    isFetching,
    isError,
    error,
    isSuccess: queries.length > 0 && queries.every((q) => q.isSuccess),
    dealCrawlerById,
    dealCrawlerSailingById,
  };
}
